import type { ReactNode, Ref } from "react"
import { forwardRef } from "react"
import Tooltip, {
  type TooltipContentProps,
  type TooltipProps,
} from "@/v2-ui/tooltip"
import Button, { type ButtonProps } from "./Button"

// Omit strips React's global `content?: string` HTML attribute so our
// ReactNode-typed tooltip content doesn't intersect with it.
export type TooltipButtonProps = Omit<ButtonProps, "content" | "label"> & {
  /** aria-label for icon-only buttons; doubles as the tooltip fallback. */
  label?: string
  content?: ReactNode
  /**
   * Overrides the default visibility. By default the tooltip shows whenever
   * `content` is supplied (even on labelled buttons) or the button is
   * icon-only with a `label`.
   */
  showTooltip?: boolean
  contentClassName?: string
  tooltipContentSide?: TooltipContentProps["side"]
  tooltipContentSideOffset?: TooltipContentProps["sideOffset"]
  /** Controlled tooltip state; leave undefined for hover behavior. */
  open?: TooltipProps["open"]
  tooltipDelayDuration?: TooltipProps["delayDuration"]
}

/**
 * Our standalone Button wrapped in a v2-ui Tooltip (the tooltip primitive
 * is fine to reuse — it's the button styling we own; see Button.tsx).
 * The trigger uses asChild, so the ref and any composed Radix props
 * (Popover.Anchor, Popover.Trigger) land on the real `<button>` element.
 */
function TooltipButton(props: TooltipButtonProps, ref: Ref<HTMLButtonElement>) {
  const {
    label,
    content,
    showTooltip,
    contentClassName,
    tooltipContentSide = "top",
    tooltipContentSideOffset,
    open,
    tooltipDelayDuration,
    ...buttonProps
  } = props

  const shouldShowTooltip =
    showTooltip ?? (content != null || buttonProps.children == null)
  const tooltipBody = content ?? label

  const button = <Button ref={ref} aria-label={label} {...buttonProps} />

  if (!shouldShowTooltip || tooltipBody == null) return button

  return (
    <Tooltip open={open} delayDuration={tooltipDelayDuration}>
      <Tooltip.Trigger asChild>{button}</Tooltip.Trigger>
      <Tooltip.Content
        side={tooltipContentSide}
        sideOffset={tooltipContentSideOffset}
        className={contentClassName}
      >
        {tooltipBody}
      </Tooltip.Content>
    </Tooltip>
  )
}

export default forwardRef<HTMLButtonElement, TooltipButtonProps>(TooltipButton)
